/**
 * `skaile source …` — manage github sources for the current project.
 *
 * A *source* is a third-party github repo of AI assets. The project's
 * `skaile.yaml` `sources:` array is the source of truth for which sources
 * this project uses. The clone itself lives at
 * `~/.skaile/cache/sources/<host>/<owner>/<repo>/` (machine-global cache, shared
 * across projects, URL-keyed so cross-org repos never collide), and the manifest is
 * mirrored into `~/.skaile/index.db` (machine-global manifest cache).
 *
 * @docLink cli/commands/source
 */

import { spawnSync } from "node:child_process";
import { existsSync, mkdirSync, renameSync, rmSync } from "node:fs";
import * as path from "node:path";
import {
  findWorkspaceRoot,
  getGlobalCacheDir,
  type SourceEntry,
  sourceCacheKey,
  sourceShortName,
  storeManifestSlug,
  WorkspaceYamlEditor,
  workspaceConfigFilename,
} from "@skaile/workspaces/core";
import { getStoreManifestsDir, LocalCatalogSource } from "@skaile/workspaces/library";
import * as p from "@clack/prompts";
import { Command } from "commander";
import { logErr, logInfo, logOk, logWarn, S } from "../helpers.ts";
import { openLibraryManager } from "../open-library.ts";
import { createFullRegistry } from "../open-registry.ts";
import { addSourceManifestCommands } from "./source-manifest.ts";

/**
 * Clone dir for one source cache key (`<host>/<owner>/<repo>`). Exported for the
 * cache-path regression test. Callers pass `sourceCacheKey(url)`.
 */
export function sourceClonePath(key: string): string {
  return path.join(getGlobalCacheDir(), key);
}

const SKAILE_MANIFEST_FILENAME = "skaile.manifest.yaml";
const LEGACY_SOURCE_CONFIG_FILENAME = ".skaile-source.yaml";

/**
 * Whether a manifest-cache refresh actually failed to index something. Keyed on
 * `errors`, NOT on `assetsUpdated < assetsFound`: the discovery walk counts
 * presets (and other intentionally-not-cataloged kinds) toward `assetsFound` but
 * never upserts them, so a source shipping a `.preset.yaml` would otherwise read
 * as a partial failure — false `! N asset(s) failed to index` + exit 1 — with
 * zero real errors.
 */
export function indexingHadFailures(result: { errors: string[] }): boolean {
  return result.errors.length > 0;
}

/**
 * A clone that ships only the legacy `.skaile-source.yaml` (no
 * `skaile.manifest.yaml`) has its declared `publisher_default` silently ignored
 * — discovery only reads `skaile.manifest.yaml`, so publisher resolution falls
 * back to the first path segment. Surface a hint so the author can fix it.
 */
export function legacySourceConfigHint(dest: string): string | undefined {
  const hasLegacy = existsSync(path.join(dest, LEGACY_SOURCE_CONFIG_FILENAME));
  const hasManifest = existsSync(path.join(dest, SKAILE_MANIFEST_FILENAME));
  if (hasLegacy && !hasManifest) {
    return (
      `This source ships a legacy ${LEGACY_SOURCE_CONFIG_FILENAME} but no ` +
      `${SKAILE_MANIFEST_FILENAME}; its declared publisher is ignored and assets ` +
      `are published under their first path segment. Add a ${SKAILE_MANIFEST_FILENAME} ` +
      `(\`skaile source manifest init\`) to set the publisher and asset inventory.`
    );
  }
  return undefined;
}

/**
 * Normalize a `source add` argument into a clonable git URL. Full URLs and
 * scp-style (`git@host:…`) specs pass through unchanged; an `owner/repo`
 * shorthand and a bare repo name expand to GitHub over SSH — a bare name
 * defaults to the `skaile-ai` org (`git@github.com:skaile-ai/<name>.git`).
 */
export function normalizeSourceUrl(input: string): string {
  const s = input.trim();
  if (/^(?:https?|git|ssh|file):\/\//.test(s) || /^git@/.test(s)) return s;
  const repo = s.replace(/\.git$/, "");
  if (/^[\w.-]+\/[\w.-]+$/.test(repo)) return `git@github.com:${repo}.git`;
  if (/^[\w.-]+$/.test(repo)) return `git@github.com:skaile-ai/${repo}.git`;
  return s;
}

/**
 * Match a stored source against a user-supplied key. Mirrors the shortcut
 * expansion `source add` applies (`normalizeSourceUrl`) so any form that `add`
 * accepted — bare name, `owner/repo`, `.git`-suffixed, full URL — also resolves
 * at `sync`/`show`/`remove` time, not just the raw stored URL or derived slug.
 */
export function sourceMatchesKey(entry: SourceEntry, key: string): boolean {
  if (entry.url === key) return true;
  if (sourceCacheKey(entry.url) === key || sourceShortName(entry.url) === key) return true;
  return entry.url === normalizeSourceUrl(key);
}

/**
 * Find an already-registered source that maps to the same cache key as `url`
 * but is stored under a *different* URL string (e.g. the `.git`-suffixed or
 * scp form of the same remote). Two such entries would fight over one clone
 * dir, so `source add` rejects the ambiguity. Returns the colliding entry, or
 * `undefined` when the URL is unique or only matches an exact-string duplicate
 * (which `setSource` updates in place).
 */
export function findCacheKeyCollision(
  sources: SourceEntry[],
  url: string,
): SourceEntry | undefined {
  const key = sourceCacheKey(url);
  return sources.find((s) => s.url !== url && sourceCacheKey(s.url) === key);
}

function requireProjectYamlPath(): string {
  const root = findWorkspaceRoot(process.cwd());
  if (!root) {
    logErr(
      "No skaile.yaml found in the current directory or any parent. " + "Run `skaile init` first.",
    );
    process.exit(1);
  }
  return path.join(root, workspaceConfigFilename());
}

export function readProjectSources(yamlPath: string): SourceEntry[] {
  if (!existsSync(yamlPath)) return [];
  try {
    return WorkspaceYamlEditor.load(yamlPath).getSources();
  } catch (err) {
    logErr(`Could not read ${yamlPath}: ${err instanceof Error ? err.message : String(err)}`);
    process.exit(1);
  }
}

/**
 * Clone the source if its clone directory does not yet exist. When `force` is
 * set, the existing clone is replaced by a fresh checkout. Returns true when a
 * clone happened. The cache slug is derived from the URL.
 *
 * A `--force` re-clone moves the old clone aside first and only deletes it once
 * the fresh clone succeeds — so a failed re-clone restores the previous clone
 * rather than leaving the source registered with no cache behind it.
 */
export function ensureClone(entry: SourceEntry, opts: { force?: boolean } = {}): boolean {
  const dest = sourceClonePath(sourceCacheKey(entry.url));
  let backup: string | undefined;
  if (existsSync(dest)) {
    if (!opts.force) return false;
    backup = `${dest}.bak`;
    rmSync(backup, { recursive: true, force: true });
    renameSync(dest, backup);
  }
  mkdirSync(path.dirname(dest), { recursive: true });
  const args = ["clone", entry.url, dest];
  // `pin` may be a branch/tag; pass it as the clone branch when present.
  if (entry.pin) args.splice(1, 0, "--branch", entry.pin);
  const clone = spawnSync("git", args, { stdio: "inherit" });
  if (clone.status !== 0) {
    if (backup) {
      rmSync(dest, { recursive: true, force: true });
      renameSync(backup, dest);
    }
    logErr(`git clone failed for ${entry.url}`);
    process.exit(1);
  }
  if (backup) rmSync(backup, { recursive: true, force: true });
  return true;
}

/**
 * Refresh the machine-global manifest cache for `entry` by running discovery
 * and upserting AssetDefinitions into `~/.skaile/index.db`. Creates the
 * libraries-table cache row if missing (ownership: reader = cache state).
 */
async function refreshManifestCache(
  entry: SourceEntry,
  includeDev = false,
): Promise<{
  assetsFound: number;
  assetsUpdated: number;
  errors: string[];
  advisories: string[];
}> {
  const { manager, library, close } = await openLibraryManager();
  try {
    const name = sourceShortName(entry.url);
    const dest = sourceClonePath(sourceCacheKey(entry.url));
    const existing = (await manager.listLibraries()).find((l) => l.name === name);
    const lib =
      existing ??
      (await manager.addLibrary({
        name,
        path: dest,
        backend: "git",
        backendConfig: { url: entry.url, branch: entry.pin ?? "main", authHint: "ssh" },
        ownership: "reader",
      }));
    // Curated overlay: a `skaile source manifest init` writes a curated
    // manifest to the local store at `~/.skaile/store/manifests/<slug>.yaml`.
    // When present, route discovery through it so the curated publisher +
    // asset inventory win over the upstream repo's raw glob discovery. Keyed by
    // the collision-free `storeManifestSlug` (matches the init writer + resolver).
    const overlayFile = path.join(getStoreManifestsDir(), `${storeManifestSlug(entry.url)}.yaml`);
    const catalog = new LocalCatalogSource(library, lib.id, dest, createFullRegistry(), {
      sidecarManifestFile: existsSync(overlayFile) ? overlayFile : undefined,
    });
    const result = await catalog.sync({ includeDev });
    await library.syncSource(lib.id);
    return result;
  } finally {
    close();
  }
}

/**
 * Auto-sync hook for `skaile search` on an unsynced workspace. Clones/pulls and
 * re-indexes every source declared in the project's `skaile.yaml`, then returns
 * whether the manifest cache may have changed (caller re-queries on `true`).
 *
 * Unlike `source sync`, this degrades gracefully — a clone/pull/index failure is
 * logged and skipped (never `process.exit`) so a failed auto-sync falls back to
 * the "run sync first" hint rather than killing the search. Returns `false`
 * (silently, no announce) when no sources are declared. Callers must guard
 * air-gapped mode themselves — this always touches the network.
 */
export async function autoSyncForSearch(projectDir: string): Promise<boolean> {
  const root = findWorkspaceRoot(projectDir);
  if (!root) return false;
  if (readProjectSources(path.join(root, workspaceConfigFilename())).length === 0) return false;

  logInfo("sources not synced — syncing now…");
  const { synced } = await syncProjectSources(projectDir);
  return synced > 0;
}

/**
 * Clone-or-pull every source declared in the project's `skaile.yaml`, then
 * refresh its manifest cache. Shared by `autoSyncForSearch` and `skaile update`
 * (the latter offers it behind a confirm) so a re-deploy can see upstream
 * commits — `update` re-deploys against the local source cache, which is
 * otherwise frozen at the commit captured when the source was first cloned.
 *
 * Graceful by design: a clone/pull/index failure for one source is logged and
 * skipped (never `process.exit`), so a single bad remote can't abort the run.
 * Existing clones are advanced with `git pull --ff-only` (no `--depth=1`, unlike
 * the installer's `pullRepo`, so it fast-forwards instead of tripping the
 * shallow-divergence path). Returns a count summary for the caller to report.
 */
export async function syncProjectSources(
  projectDir: string,
  opts: { dev?: boolean } = {},
): Promise<{ total: number; synced: number; failed: number }> {
  const root = findWorkspaceRoot(projectDir) ?? projectDir;
  const sources = readProjectSources(path.join(root, workspaceConfigFilename()));
  let synced = 0;
  let failed = 0;
  for (const entry of sources) {
    const slug = sourceShortName(entry.url);
    const dest = sourceClonePath(sourceCacheKey(entry.url));
    try {
      // Quiet git — the sync is a preparatory step; keep progress bars out of
      // the foreground output (spinner / install result).
      if (!existsSync(dest)) {
        mkdirSync(path.dirname(dest), { recursive: true });
        const args = ["clone", entry.url, dest];
        if (entry.pin) args.splice(1, 0, "--branch", entry.pin);
        const clone = spawnSync("git", args, { stdio: "ignore" });
        if (clone.status !== 0) {
          logWarn(`${slug}: git clone failed, skipping.`);
          failed++;
          continue;
        }
      } else {
        const pull = spawnSync("git", ["pull", "--ff-only"], { cwd: dest, stdio: "ignore" });
        if (pull.status !== 0) {
          logWarn(`${slug}: git pull failed, skipping manifest refresh.`);
          failed++;
          continue;
        }
      }
      const result = await refreshManifestCache(entry, opts.dev);
      for (const err of result.errors) logWarn(`${slug}: ${err}`);
      for (const advisory of result.advisories) logInfo(`${slug}: ${advisory}`);
      synced++;
    } catch (err) {
      logWarn(`${slug}: sync failed — ${err instanceof Error ? err.message : String(err)}`);
      failed++;
    }
  }
  return { total: sources.length, synced, failed };
}

/** Remove the libraries-table cache row and (optionally) the clone bytes. */
async function dropManifestCache(url: string, purgeClone: boolean): Promise<void> {
  const name = sourceShortName(url);
  const { manager, close } = await openLibraryManager();
  try {
    const existing = (await manager.listLibraries()).find((l) => l.name === name);
    if (existing) {
      await manager.removeLibrary(name, { purge: false });
    }
  } finally {
    close();
  }
  if (purgeClone) {
    const dest = sourceClonePath(sourceCacheKey(url));
    if (existsSync(dest)) rmSync(dest, { recursive: true, force: true });
  }
}

export function makeSourceCommand(): Command {
  const cmd = new Command("source").description(
    "Manage github sources for the current project (recorded in skaile.yaml)",
  );

  // ── source add ──────────────────────────────────────────────────────────
  cmd
    .command("add <source>")
    .description(
      "Clone a github repo and register it in this project's skaile.yaml. " +
        "<source> may be a full git URL, an owner/repo shorthand, or a bare " +
        "name (→ git@github.com:skaile-ai/<name>.git).",
    )
    .option("--name <slug>", "[deprecated] no-op — the source entry stores only the URL")
    .option("--pin <ref>", "Pin a branch, tag, or 40-char SHA")
    .option("--branch <branch>", "[deprecated] alias for --pin")
    .option("--force", "Remove an existing clone and re-clone", false)
    .option("--dev", "Also index dev-only assets under the source's dev_paths", false)
    .action(
      async (
        source: string,
        opts: {
          name?: string;
          pin?: string;
          branch?: string;
          force?: boolean;
          dev?: boolean;
        },
      ) => {
        const url = normalizeSourceUrl(source);
        const yamlPath = requireProjectYamlPath();

        // A different URL string that maps to the same cache key (e.g. the
        // `.git`-suffixed or scp form of an already-registered remote) would
        // fight over one clone dir. setSource only dedupes exact strings, so
        // reject the ambiguous collision here instead of silently shadowing.
        const collision = findCacheKeyCollision(readProjectSources(yamlPath), url);
        if (collision) {
          logErr(
            `"${url}" maps to the same cache key (${sourceCacheKey(url)}) as ` +
              `already-registered "${collision.url}". Remove that source first ` +
              `(skaile source remove) or re-use its exact URL to update it.`,
          );
          process.exitCode = 1;
          return;
        }

        if (opts.name) {
          logWarn(
            "the --name flag is deprecated; the source entry now stores only the URL " +
              "(the cache slug is derived from it).",
          );
        }
        const pin = opts.pin ?? opts.branch;
        const slug = sourceShortName(url);
        const entry: SourceEntry = { url };
        if (pin) entry.pin = pin;

        const dest = sourceClonePath(sourceCacheKey(url));
        const preexisting = existsSync(dest);
        // Clone/validate the repo BEFORE touching skaile.yaml — a failed clone
        // (repo not found, bad URL, network error) hard-exits in ensureClone, so
        // the entry is only ever recorded once the source is known-good.
        const cloned = ensureClone(entry, { force: opts.force });
        if (!cloned) {
          logOk(`Reusing cached clone at ${dest}`);
        } else if (preexisting) {
          logOk(`Re-cloned ${url} → ${dest}`);
        } else {
          logOk(`Cloned ${url} → ${dest}`);
        }

        const editor = WorkspaceYamlEditor.load(yamlPath);
        editor.setSource(entry);
        editor.save();

        const result = await refreshManifestCache(entry, opts.dev);
        const failed = indexingHadFailures(result);
        const indexedMsg = `Indexed ${result.assetsUpdated} of ${result.assetsFound} asset(s) from "${slug}".`;
        if (failed) {
          logWarn(indexedMsg);
        } else {
          logOk(indexedMsg);
        }
        for (const err of result.errors) logWarn(err);
        for (const advisory of result.advisories) logInfo(advisory);

        const legacyHint = legacySourceConfigHint(dest);
        if (legacyHint) logWarn(legacyHint);

        const where = path.relative(process.cwd(), yamlPath);
        if (failed) {
          // The entry IS recorded in skaile.yaml, but indexing did not fully
          // succeed — don't present it as a clean success.
          logWarn(
            `Source "${slug}" registered in ${where}, but ${result.errors.length} ` +
              `asset(s) failed to index (see above). Fix the cause, then re-run ` +
              `\`skaile source sync ${slug}\`.`,
          );
          process.exitCode = 1;
        } else {
          logOk(`Source "${slug}" registered in ${where}.`);
        }
      },
    );

  // ── source list ─────────────────────────────────────────────────────────
  cmd
    .command("list")
    .description("List sources registered in this project's skaile.yaml")
    .option("--json", "Output as JSON")
    .action(async (opts: { json?: boolean }) => {
      const yamlPath = requireProjectYamlPath();
      const sources = readProjectSources(yamlPath);
      if (opts.json) return console.log(JSON.stringify(sources, null, 2));
      if (sources.length === 0) {
        logInfo("No sources in skaile.yaml. Run `skaile source add <git-url>` to register one.");
        return;
      }
      console.log();
      console.log(S.heading("  Sources"));
      for (const s of sources) {
        const slug = sourceShortName(s.url);
        const cached = existsSync(sourceClonePath(sourceCacheKey(s.url)))
          ? ""
          : S.warn(" (not cached)");
        console.log(`  ${S.cmd(slug.padEnd(24))}  ${S.dim(s.url)}${cached}`);
      }
      console.log(`\n  ${S.dim(`${sources.length} source(s)`)}\n`);
    });

  // ── source show ─────────────────────────────────────────────────────────
  cmd
    .command("show <name-or-url>")
    .description("Show details for a source (matched by URL or derived slug)")
    .action(async (key: string) => {
      const yamlPath = requireProjectYamlPath();
      const entry = readProjectSources(yamlPath).find((s) => sourceMatchesKey(s, key));
      if (!entry) {
        logErr(`No source matching "${key}" in ${yamlPath}.`);
        process.exit(1);
      }
      const slug = sourceShortName(entry.url);
      const clonePath = sourceClonePath(sourceCacheKey(entry.url));
      console.log(
        JSON.stringify(
          {
            ...entry,
            slug,
            clonePath,
            cached: existsSync(clonePath),
          },
          null,
          2,
        ),
      );
    });

  // ── source remove ───────────────────────────────────────────────────────
  cmd
    .command("remove <name-or-url>")
    .description("Remove a source from this project's skaile.yaml (matched by URL or slug)")
    .option("--purge", "Also delete the cached clone from ~/.skaile/cache/sources/", false)
    .action(async (key: string, opts: { purge: boolean }) => {
      const yamlPath = requireProjectYamlPath();
      const editor = WorkspaceYamlEditor.load(yamlPath);
      // Resolve to the stored URL (entries are keyed by url in the editor).
      const match = editor.getSources().find((s) => sourceMatchesKey(s, key));
      if (!match) {
        logErr(`No source matching "${key}" in ${yamlPath}.`);
        process.exit(1);
      }
      const slug = sourceShortName(match.url);
      editor.removeSource(match.url);
      editor.save();
      await dropManifestCache(match.url, opts.purge);
      logOk(
        opts.purge
          ? `Removed source "${slug}" and purged its clone.`
          : `Removed source "${slug}" from skaile.yaml (clone left in ~/.skaile/cache/sources/ — pass --purge to remove it, or run \`skaile cache clean\`).`,
      );
    });

  // ── source sync ─────────────────────────────────────────────────────────
  cmd
    .command("sync [name]")
    .description("Pull upstream changes and refresh the manifest cache")
    .option("--dev", "Also index dev-only assets under each source's dev_paths", false)
    .option("--all", "Sync every source without prompting", false)
    .action(async (key: string | undefined, opts: { dev?: boolean; all?: boolean }) => {
      const yamlPath = requireProjectYamlPath();
      const sources = readProjectSources(yamlPath);
      if (sources.length === 0) {
        logInfo("No sources in skaile.yaml.");
        return;
      }

      let targets: SourceEntry[];
      if (key) {
        targets = sources.filter((s) => sourceMatchesKey(s, key));
        if (targets.length === 0) {
          logInfo(`No source matching "${key}".`);
          return;
        }
      } else if (opts.all || sources.length === 1 || !process.stdin.isTTY) {
        // --all, a single source, or a non-interactive shell: sync everything.
        targets = sources;
      } else {
        const selected = await p.multiselect({
          message: "Select sources to sync",
          options: sources.map((s) => ({
            value: s.url,
            label: sourceShortName(s.url),
            hint: s.url,
          })),
          required: true,
        });
        if (p.isCancel(selected)) {
          p.cancel("Cancelled.");
          return;
        }
        const picked = new Set(selected as string[]);
        targets = sources.filter((s) => picked.has(s.url));
      }
      for (const entry of targets) {
        const slug = sourceShortName(entry.url);
        const dest = sourceClonePath(sourceCacheKey(entry.url));
        if (!existsSync(dest)) {
          ensureClone(entry);
          logOk(`${slug}: cloned to ${dest}.`);
        } else {
          const pull = spawnSync("git", ["pull", "--ff-only"], { cwd: dest, stdio: "inherit" });
          if (pull.status !== 0) {
            logWarn(`${slug}: git pull failed, skipping manifest refresh.`);
            continue;
          }
        }
        const result = await refreshManifestCache(entry, opts.dev);
        const failed = indexingHadFailures(result);
        const indexedMsg = `${slug}: indexed ${result.assetsUpdated} of ${result.assetsFound} asset(s).`;
        if (failed) {
          logWarn(indexedMsg);
          process.exitCode = 1;
        } else {
          logOk(indexedMsg);
        }
        for (const err of result.errors) logWarn(`${slug}: ${err}`);
        for (const advisory of result.advisories) logInfo(`${slug}: ${advisory}`);
        const legacyHint = legacySourceConfigHint(dest);
        if (legacyHint) logWarn(`${slug}: ${legacyHint}`);
      }
    });

  // ── source patch / propose (stubs) ──────────────────────────────────────
  cmd
    .command("patch <ref>")
    .description("Extract an asset for editing against the source")
    .action((_ref: string) => {
      logErr(
        "`skaile source patch` is scaffolded but not yet wired. " +
          "The patch flow lands in a follow-up PR.",
      );
      process.exit(2);
    });

  cmd
    .command("propose <ref>")
    .description("Open a PR with the patch against the source upstream")
    .action((_ref: string) => {
      logErr(
        "`skaile source propose` is scaffolded but not yet wired. " +
          "The propose flow lands in a follow-up PR.",
      );
      process.exit(2);
    });

  // ── source manifest * ───────────────────────────────────────────────────
  addSourceManifestCommands(cmd);

  return cmd;
}
